import './TagsInput.css';
import './../input.scss';
import React, { useState } from 'react';
import { IconName } from '../../Icon/IconName';
import { Icon } from '../../Icon/Icon';
import classnames from 'classnames';
import { Shortcut } from '../../Shortcut/Shortcut';
import { t } from '../../../locales/translate';
import { TagsGroup } from '../../Tag/TagsGroup';
import { FormField } from '../../../model/form';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';

interface Props extends FormField<string[] | null> {
  icon?: IconName;
  label?: string;
  required?: boolean;
  actions?: React.ReactNode | React.ReactNodeArray;
  description?: string;
  disabled?: boolean;
  onTagDelete?: (tag: string) => void;
  onInputChange?: (value: string) => void;
  maxWidth?: number;
}

export function FormTagsInput({
  value,
  icon,
  label,
  required,
  actions,
  description,
  disabled,
  placeholder,
  touched,
  valid,
  errors,
  onChange,
  onFocus,
  onBlur,
  onTagDelete,
  onInputChange,
  maxWidth,
}: Props): JSX.Element {
  const [active, setActive] = useState(false);
  const [pendingTag, setPendingTag] = useState(' ');
  const full = active || placeholder || !!value?.length;
  const displayErrors = touched && !valid;

  function addTag(): void {
    const tag = pendingTag.trim();
    if (!tag) {
      return;
    }
    if (value?.length) {
      const exists = value.includes(tag);
      if (!exists) {
        onChange?.([...value, tag]);
      }
    } else {
      onChange?.([tag]);
    }
    setPendingTag(' ');
    onInputChange?.('');
  }

  function removeLastTag(): void {
    if (value && !pendingTag) {
      if (onTagDelete) {
        onTagDelete(value[value.length - 1]);
      }

      onChange?.([...value.filter((_, i) => i !== value.length - 1)]);
    }
  }

  function removeTagAtIndex(index: number): void {
    if (!value) {
      return;
    }

    if (onTagDelete) {
      onTagDelete(value[index]);
    }
    onChange?.([...value.filter((_, i) => i !== index)]);
  }

  return (
    <div className={classnames('input tags-input', { 'input--with-icon': icon })} style={{ maxWidth }}>
      {icon && <Icon name={icon} className="input__icon" />}
      <div
        className={classnames('input__container', {
          'input__container--active': active,
          'input__container--full': full,
        })}
      >
        <div className="input__input-wrapper">
          {label && (
            <label className="input__label">
              {label}
              {!required && ` (${t.general.optional})`}
            </label>
          )}
          <TagsGroup
            tags={value?.map((tag, index) => ({
              id: index.toString(),
              label: tag,
            }))}
            onRemove={removeTagAtIndex}
            input={
              <input
                className="input__input"
                type="text"
                disabled={disabled}
                onChange={(e) => {
                  onInputChange?.(e.currentTarget.value);
                  setPendingTag(e.currentTarget.value);
                }}
                value={pendingTag}
                onFocus={() => {
                  setActive(true);
                  onFocus?.();
                }}
                onBlur={() => {
                  addTag();
                  setActive(false);
                  onBlur?.();
                }}
              />
            }
            expandHiddenTags="bottom-end"
            onClear={() => {
              onChange?.([]);
            }}
          />
          {actions && <div className="input__actions">{actions}</div>}
        </div>
        <div className="input__description-wrapper">
          {description && <div className="input__description">{description}</div>}
        </div>
        <Collapse open={displayErrors}>
          {errors?.map((error) => {
            return (
              <div className="input__error-message" key={error}>
                {t.errors[error] || error}
              </div>
            );
          })}
        </Collapse>
      </div>
      <Shortcut keys="Enter" onInvoke={addTag} overrideInput={active} />
      <Shortcut keys=" " onInvoke={addTag} overrideInput={active} />
      <Shortcut keys="Backspace" onInvoke={removeLastTag} overrideInput={active} />
    </div>
  );
}
